// src/auth/roles.ts
export type UserRole = 'admin' | 'employee' | 'user' | 'audit';

export const ROLES: readonly UserRole[] = ['admin', 'employee', 'user', 'audit'];

// Roles assignable via the invite/edit UI (admin is console-only).
export const ASSIGNABLE_ROLES: readonly UserRole[] = ['employee', 'user', 'audit'];

// Known tab keys. Used by UI checkboxes and by getEffectiveTabs.
export const KNOWN_FEATURES = [
    'assets',
    'events',
    'portfolio',
    'simulation',
    'monitor',
    // `api` is deliberately NOT a tab. It grants a user the right to hold
    // partner API keys for the indexer's /v1 surface, and it is never a role
    // default — including for admin. It must be granted per user, explicitly.
    // The one exception lives in key minting, not here: an approved admin may
    // hold a key without it (canHoldKey in src/pages/admin/apiKeys.ts and
    // functions/admin/adminCreateApiKey.js, which writes `api` into that
    // key's own feature copy).
    'api',
    // `researcher` is not a tab either. It adds the event clock to the event
    // view (src/pages/markets/event/clock/) and, like `api`, is never a role
    // default: it is granted per user. Admins get it through canUseFeature's
    // bypass.
    'researcher',
] as const;
export type KnownFeature = (typeof KNOWN_FEATURES)[number];

// Role-driven tab defaults. Admin maps to the full set of TABS by design (no
// special-case bypass elsewhere). Note this is an explicit literal, not
// KNOWN_FEATURES — `api` is in KNOWN_FEATURES but must never be a role
// default, so deriving this list from KNOWN_FEATURES would silently grant
// partner API access to every admin. See roles.parity.test.ts.
export const ROLE_DEFAULT_TABS: Record<UserRole, readonly KnownFeature[]> = {
    admin: ['assets', 'events', 'portfolio', 'simulation', 'monitor'],
    employee: ['assets', 'events', 'portfolio', 'monitor'],
    user: [],
    audit: ['portfolio'],
};

// Transitional: user docs written before the split still hold `markets`.
// Expanding it here means new code tolerates un-migrated docs, so deploy
// and migration need no ordering. Remove once the migration has run.
export const LEGACY_MARKETS = 'markets';

function expandLegacy(features: readonly string[]): string[] {
    return features.flatMap(f =>
        f === LEGACY_MARKETS ? ['assets', 'events'] : [f]
    );
}

// Effective tab list = role default ∪ allowed_features. Add-only:
// allowed_features can never subtract from the role default. Throws
// on an unknown role rather than returning an empty default — missing
// role is a deny condition handled by the caller.
export function getEffectiveTabs(
    role: UserRole,
    allowed_features?: readonly string[]
): string[] {
    const defaults = ROLE_DEFAULT_TABS[role];
    if (!defaults) {
        throw new Error(`unknown_role:${role}`);
    }
    return Array.from(
        new Set([...defaults, ...expandLegacy(allowed_features ?? [])])
    );
}
